import { useCallback } from 'react';

import { useCrossProductUrlWrapper } from '@atlaskit/analytics-cross-product';
import { useSmartLinkContext } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';

import { useGlobalConfig } from '../global-config/useGlobalConfig';

const FALLBACK_BRIDGE = 'rovo-chat';

export type XpcWrapUrl = (url: string, stripOnly: boolean) => string;

/**
 * Returns a function that processes URLs for cross-product interaction-tracking (XPC)
 * in Rovo chat responses.
 *
 * Reads `bridgeProduct`, `xpcProduct`, and `xpcSubProduct` from `SmartCardContext`
 * (configured at `<SmartCardProvider>` in main.tsx) so the same precedence rules
 * and dynamic bridge string (e.g. `rovo-chat.fullscreen`, `rovo-chat.sidebar`)
 * used by the linking platform's smart-link wrapper apply here too.
 *
 * Behaviour is controlled per-call via the required `stripOnly` argument:
 * - `stripOnly: false` — strip `datasource` param AND apply full XPC URL wrapping.
 *   Use for plain links (not rendered as smart links).
 * - `stripOnly: true` — only strip the `datasource` param.
 *   Use for links rendered as smart links by the linking platform, which already
 *   handle their own analytics.
 *
 * Returns `undefined` when the feature gate is off so callers can skip expensive
 * code paths (e.g. the slow ADFEncoder instantiation in encodeAdf).
 * Returns the URL unchanged when it is not parseable or has no `?datasource=1p`.
 */
export const useXpcLinkWrapper = (): XpcWrapUrl | undefined => {
	const { productKey } = useGlobalConfig();
	const { bridgeProduct, xpcProduct, xpcSubProduct } = useSmartLinkContext();

	// Mirror the precedence used by `useSmartLinkCrossProductUrlWrapper`:
	// xpcProduct overrides productKey for XPC analytics only.
	// Treat empty strings as missing so the wrapper always receives a usable product.
	const effectiveProduct = xpcProduct || productKey || FALLBACK_BRIDGE;

	const wrapUrl = useCrossProductUrlWrapper({
		bridge: bridgeProduct ?? FALLBACK_BRIDGE,
		product: effectiveProduct,
		...(xpcSubProduct ? { subProduct: xpcSubProduct } : {}),
	});

	const isEnabled = fg('platform_smartlink_xpc_url_wrapping');

	const callback = useCallback(
		(url: string, stripOnly: boolean) => {
			try {
				const parsed = new URL(url);
				if (parsed.searchParams.get('datasource') !== '1p') {
					return url;
				}
				parsed.searchParams.delete('datasource');
				if (stripOnly) {
					return parsed.toString();
				}
				return wrapUrl(parsed.toString());
			} catch {
				// Non-parseable URL — return as-is
				return url;
			}
		},
		[wrapUrl],
	);

	return isEnabled ? callback : undefined;
};
